/**
 * @fileoverview Login Form Component
 *
 * Renders the main login form with username and password fields.
 */

import React from 'react';
import {
  FiUser,
  FiLock,
  FiEye,
  FiEyeOff,
  FiShield,
  FiX,
  FiBriefcase
} from 'react-icons/fi';
import { isWebAuthnSupported } from '../../../utils/webauthn';
import { useOrganization } from '../../../context/OrganizationContext';

/**
 * Login Form Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.formik - Formik form instance
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.showPassword - Password visibility state
 * @param {Function} props.onTogglePassword - Toggle password visibility
 * @param {Function} props.onForgotPassword - Handle forgot password click
 * @param {Array} props.recentUsers - List of recent users
 * @param {Object} props.quickLoginUser - Currently quick-logging in user
 * @param {boolean} props.quickLoginLoading - Quick login loading state
 * @param {Function} props.onSelectRecentUser - Handle recent user selection
 * @param {Function} props.onRemoveRecentUser - Handle recent user removal
 * @returns {JSX.Element} Login form component
 */
const LoginForm = ({
  formik,
  loading,
  showPassword,
  onTogglePassword,
  onForgotPassword,
  recentUsers = [],
  quickLoginUser,
  quickLoginLoading,
  onSelectRecentUser,
  onRemoveRecentUser
}) => {
  const { organizationConfig, currentOrganization } = useOrganization();

  return (
    <div className="lp-login-card">
      {/* Organization Badge */}
      {currentOrganization && (
        <div className="lp-org-badge">
          <FiBriefcase className="lp-org-badge-icon" />
          <span className="lp-org-badge-name">
            {organizationConfig?.name || currentOrganization}
          </span>
        </div>
      )}

      <div className="lp-card-header">
        <h1>Welcome back</h1>
        <p>Sign in to your Clockwise account</p>
      </div>

      <form onSubmit={formik.handleSubmit} className="lp-form">
        <div className="lp-form-group">
          <label className="lp-form-label">Username</label>
          <div className="lp-input-wrapper">
            <FiUser className="lp-input-icon" />
            <input
              type="text"
              id="username"
              name="username"
              placeholder="Enter your username"
              className={`lp-form-input ${formik.touched.username && formik.errors.username ? 'lp-input-error' : ''}`}
              value={formik.values.username}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={loading}
            />
          </div>
          {formik.touched.username && formik.errors.username && (
            <div className="lp-error-message">{formik.errors.username}</div>
          )}
        </div>

        <div className="lp-form-group">
          <label className="lp-form-label">Password</label>
          <div className="lp-input-wrapper">
            <FiLock className="lp-input-icon" />
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              placeholder="Enter your password"
              className={`lp-form-input ${formik.touched.password && formik.errors.password ? 'lp-input-error' : ''}`}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={loading}
              style={{ paddingRight: '48px' }}
            />
            <button
              type="button"
              className="lp-password-toggle"
              onClick={onTogglePassword}
              aria-label={showPassword ? "Hide password" : "Show password"}
              disabled={loading}
            >
              {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
            </button>
          </div>
          {formik.touched.password && formik.errors.password && (
            <div className="lp-error-message">{formik.errors.password}</div>
          )}
        </div>

        <div className="lp-form-footer">
          <label className="lp-remember-me">
            <input type="checkbox" defaultChecked />
            Remember me
          </label>
          <button
            type="button"
            className="lp-forgot-link"
            onClick={onForgotPassword}
            disabled={loading}
          >
            Forgot password?
          </button>
        </div>

        <button
          type="submit"
          className="lp-btn lp-btn-primary"
          disabled={loading}
        >
          {loading ? 'Signing In...' : 'Sign In'}
        </button>
      </form>

      {/* Recent Users Section */}
      {recentUsers.length > 0 && (
        <>
          <div className="lp-divider">or continue with</div>

          <div className="lp-recent-users">
            <div className="lp-recent-users-label">Recent Users</div>
            <div className="lp-recent-user-list">
              {recentUsers.map((recentUser) => (
                <div
                  key={recentUser.username}
                  className={`lp-recent-user ${quickLoginUser?.username === recentUser.username ? 'lp-recent-user-loading' : ''}`}
                  onClick={() => !quickLoginLoading && onSelectRecentUser(recentUser)}
                >
                  <div className="lp-user-avatar">
                    {recentUser.firstName?.[0] || recentUser.username[0].toUpperCase()}
                  </div>
                  <div className="lp-user-info">
                    <div className="lp-user-name">
                      {recentUser.firstName && recentUser.lastName
                        ? `${recentUser.firstName} ${recentUser.lastName}`
                        : recentUser.username}
                    </div>
                    <div className="lp-user-email">@{recentUser.username}</div>
                  </div>
                  {recentUser.hasPasskey && isWebAuthnSupported() && (
                    <div className="lp-passkey-badge">
                      <FiShield size={14} />
                      Passkey
                    </div>
                  )}
                  <button
                    type="button"
                    className="lp-recent-user-remove"
                    onClick={(e) => onRemoveRecentUser(e, recentUser.username)}
                    title="Remove from recent"
                  >
                    <FiX size={14} />
                  </button>
                  {quickLoginUser?.username === recentUser.username && (
                    <div className="lp-recent-user-spinner"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LoginForm;
